import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { api } from '../../api/chat';
import { refreshContacts, refreshConversations, refreshPendingRequests } from '../../redux/chat.redux';
import type { User } from '../../types/user';
import { avatarText } from '../../utils/format';
import PanelShell from './panel-shell';

const GREETING_MAX = 50;

interface Props {
  onClose: () => void;
  embedded?: boolean;
}

export default function AddFriendPanel({ onClose, embedded }: Props) {
  const dispatch = useAppDispatch();
  const me = useAppSelector((s) => s.user.user);
  const contacts = useAppSelector((s) => s.chat.contacts);
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<User[]>([]);
  const [selected, setSelected] = useState<User | null>(null);
  const [greeting, setGreeting] = useState('');
  const [applying, setApplying] = useState(false);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const doSearch = async () => {
    if (!query.trim()) return;
    setSearching(true);
    setMsg(null);
    setSelected(null);
    try {
      const users = await api.searchUsers(query.trim());
      setResults(users.filter((u) => u.id !== me?.id));
    } catch (err: any) {
      setMsg({ type: 'error', text: err?.message || '搜索失败' });
    } finally {
      setSearching(false);
    }
  };

  const apply = async () => {
    if (!selected) return;
    setApplying(true);
    setMsg(null);
    try {
      const r = await api.addContact({ user_id: selected.id, greeting: greeting.trim() || undefined });
      if (r.auto_accepted) {
        setMsg({ type: 'success', text: `已与 ${selected.nickname || selected.username} 成为好友` });
        dispatch(refreshContacts());
        dispatch(refreshConversations());
      } else {
        setMsg({ type: 'success', text: '好友申请已发送,等待对方确认' });
        dispatch(refreshPendingRequests());
      }
      setSelected(null);
      setGreeting('');
    } catch (err: any) {
      setMsg({ type: 'error', text: err?.message || '添加失败' });
    } finally {
      setApplying(false);
    }
  };

  return (
    <PanelShell title="添加好友" onClose={onClose} embedded={embedded}>
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <TextField
            fullWidth
            size="small"
            label="搜索用户名"
            placeholder="输入对方的用户名"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && doSearch()}
          />
          <Button variant="contained" onClick={doSearch} disabled={searching || !query.trim()}>
            搜索
          </Button>
        </Box>
        {searching && <CircularProgress size={24} sx={{ mt: 2 }} />}

        {results.length > 0 && !selected && (
          <List sx={{ mt: 1 }}>
            {results.map((u) => {
              const isContact = contacts.some((c) => c.id === u.id);
              return (
                <ListItemButton key={u.id} onClick={() => setSelected(u)} sx={{ px: 1 }}>
                  <ListItemAvatar>
                    <Avatar src={u.avatar || undefined}>{avatarText(u)}</Avatar>
                  </ListItemAvatar>
                  <ListItemText primary={u.nickname || u.username} secondary={isContact ? '已是好友' : u.username} />
                  {isContact && (
                    <Typography variant="caption" color="text.secondary">
                      已是好友
                    </Typography>
                  )}
                </ListItemButton>
              );
            })}
          </List>
        )}

        {results.length === 0 && !searching && query.trim() && (
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2 }}>
            未找到匹配的用户
          </Typography>
        )}

        {selected && (
          <Box sx={{ mt: 2, p: 2, border: '1px solid rgba(0,0,0,0.12)', borderRadius: 2, bgcolor: '#fff' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <Avatar src={selected.avatar || undefined} sx={{ width: 44, height: 44 }}>
                {avatarText(selected)}
              </Avatar>
              <Box>
                <Typography sx={{ fontWeight: 600 }}>{selected.nickname || selected.username}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {selected.username}
                </Typography>
              </Box>
            </Box>
            <TextField
              fullWidth
              size="small"
              label="验证信息(打招呼)"
              placeholder={`你好,我是…(最多 ${GREETING_MAX} 字)`}
              value={greeting}
              onChange={(e) => setGreeting(e.target.value.slice(0, GREETING_MAX))}
              inputProps={{ maxLength: GREETING_MAX }}
              helperText={`${greeting.length}/${GREETING_MAX}`}
              sx={{ mb: 1 }}
            />
            <Button fullWidth variant="contained" onClick={apply} disabled={applying}>
              {applying ? '发送中…' : '发送申请'}
            </Button>
          </Box>
        )}

        {msg && (
          <Alert severity={msg.type} sx={{ mt: 2 }}>
            {msg.text}
          </Alert>
        )}
      </Box>
    </PanelShell>
  );
}
